import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { ArrowForward } from "@mui/icons-material";
import { AnswerFileUploadDialog } from "./AnswerFileUploadDialog";
import { IdentifierAutoComplete } from "./IdentifierAutoComplete";
import { setPdfWorker } from "../../../utils/pdfWorkerConfig";
import { ImageUploadQRDialog } from "./ImageUploadQRDialog";
import { checkIfIsIndependentUser } from "../../auth/Utils";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { useAnswerFileUploader } from "../hooks/useAnswerFileUploader";
import { useSubmitHandwrittenFile } from "../hooks/useSubmitHandwrittenFile";
import { useSubmitAnswer } from "../hooks/useSubmitAnswer";
import { hasAnswerText } from "../utils/answerSubmission";
import { FileDropZone } from "./FileDropZone";
import TieredFeatureAccess from "../../../shared-components/TieredFeatureAccess";

// Call the function to set the worker source
setPdfWorker();

const DocumentUploadSubmission = ({
  assignmentQuestion,
  identifiers,
  onSubmitAnswerText,
}) => {
  const [uploadUrl, setUploadUrl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const isIndependentUser = checkIfIsIndependentUser();
  const ApiClient = useApiClient();

  const fileUploader = useAnswerFileUploader({
    isIndependentUser: isIndependentUser,
    student: {},
  });
  const { submitHandwrittenFile } = useSubmitHandwrittenFile(
    assignmentQuestion,
    fileUploader,
    ApiClient,
  );

  const handleUploadPhotoClicked = async () => {
    setUploadUrl(null);
    try {
      const response = await ApiClient.assignment.generateUploadURL({
        assignmentQuestion: assignmentQuestion.id,
      });
      if (response.success) {
        setUploadUrl(response.url);
        setUploadDialogOpen(true);
      } else {
        enqueueSnackbar("Error Generating Upload URL- please try again!", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to Generate Upload URL", error);
      enqueueSnackbar("Error Generating Upload URL- please try again!", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <React.Fragment>
      <AnswerFileUploadDialog
        open={fileUploader.fileDialogOpen}
        setOpen={fileUploader.setFileDialogOpen}
        file={fileUploader.file}
        identifier={fileUploader.identifier}
        setIdentifier={fileUploader.setIdentifier}
        identifiers={identifiers}
        onSubmitProcessedAnswer={onSubmitAnswerText}
        onSubmitHandwrittenAnswer={submitHandwrittenFile}
      />
      <ImageUploadQRDialog
        url={uploadUrl}
        open={uploadDialogOpen}
        setOpen={setUploadDialogOpen}
      />
      <Typography variant={"h5"} sx={{ fontWeight: "medium" }}>
        Document Upload
      </Typography>
      <FormControl fullWidth>
        <FileDropZone
          files={fileUploader.file ? [fileUploader.file] : []}
          onFilesChange={fileUploader.handleFileUpload}
        />
      </FormControl>
      <Box sx={{ my: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Or
        </Typography>
      </Box>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<FruitifyIcon name={"Camera"} />}
        sx={{
          textTransform: "none",
          paddingX: 3,
          paddingY: 1.5,
        }}
        onClick={() => handleUploadPhotoClicked()}
      >
        Upload from mobile
      </Button>
    </React.Fragment>
  );
};

export const TextSubmission = ({ availableIdentifiers, onSubmit }) => {
  const [identifier, setIdentifier] = useState({});
  const [answer, setAnswer] = useState("");

  const handleSubmit = () => {
    if (!hasAnswerText(answer)) return;
    onSubmit(answer, identifier);
    setIdentifier({});
    setAnswer("");
  };

  return (
    <React.Fragment>
      <Typography variant={"subtitle1"} sx={{ fontWeight: "medium" }}>
        Paste or type the student's answer directly into the text box
      </Typography>
      <IdentifierAutoComplete
        availableIdentifiers={availableIdentifiers}
        setIdentifier={setIdentifier}
        identifier={identifier}
      />
      <FormControl fullWidth>
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            width: "100%",
          }}
        >
          <TextField
            multiline
            minRows={6}
            maxRows={16}
            fullWidth
            margin="normal"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            error={!hasAnswerText(answer)}
            helperText={!hasAnswerText(answer) ? "Answer text is required" : ""}
          />
        </Box>
      </FormControl>
      <Button
        variant={"contained"}
        fullWidth
        onClick={handleSubmit}
        disabled={!hasAnswerText(answer)}
      >
        Submit
      </Button>
    </React.Fragment>
  );
};

const AnswerSubmission = ({
  assignmentQuestion,
  identifiers,
  showSubmissionHubLink = false,
  submissionHubUrl = null,
}) => {
  const [submissionType, setSubmissionType] = useState("upload file");
  const ApiClient = useApiClient();
  const navigate = useNavigate();

  const { submitAnswer } = useSubmitAnswer(assignmentQuestion, ApiClient);
  const handleSubmit = async (answer, identifier) => {
    submitAnswer(answer, identifier);
  };

  return (
    <Card mt={1}>
      <CardContent>
        <Stack direction={"column"} spacing={2}>
          <Box>
            <Typography variant={"h5"}>Upload New Answers</Typography>
            <Typography variant={"body1"} sx={{ mt: 1 }}>
              Choose how you’d like to submit student answers.
            </Typography>
            {showSubmissionHubLink && submissionHubUrl && (
              <TieredFeatureAccess
                obj={assignmentQuestion.assignment}
                feature="bulk_upload"
                blur={false}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2">
                    For bulk uploads and advanced submission options
                  </Typography>
                  <IconButton
                    onClick={() => navigate(submissionHubUrl)}
                    size="small"
                    data-testid="submission-hub-link"
                    sx={{
                      ml: 1,
                      color: "primary.main",
                    }}
                  >
                    <ArrowForward />
                  </IconButton>
                </Stack>
              </TieredFeatureAccess>
            )}
          </Box>
          <ToggleButtonGroup
            fullWidth
            value={submissionType}
            exclusive
            onChange={(_, newSubmissionType) =>
              setSubmissionType(newSubmissionType)
            }
            aria-label="Submission Type"
          >
            <ToggleButton value="upload file">Upload File</ToggleButton>
            <ToggleButton value="type answer">Enter Answer</ToggleButton>
          </ToggleButtonGroup>
          {submissionType === "upload file" ? (
            <DocumentUploadSubmission
              identifiers={identifiers}
              assignmentQuestion={assignmentQuestion}
              onSubmitAnswerText={handleSubmit}
            />
          ) : (
            <TextSubmission
              availableIdentifiers={identifiers} // TODO: filter this to only include unused identifiers
              onSubmit={handleSubmit}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export { AnswerSubmission, DocumentUploadSubmission };
